import { CircularProgress, Divider, Grid, Paper } from '@mui/material';
import {
  GridCallbackDetails,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridInputRowSelectionModel,
  GridRowClassNameParams,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowSelectionModel,
  GridValidRowModel
} from '@mui/x-data-grid';

import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Waypoint } from 'react-waypoint';

import { SortOption } from 'types/AppTypes';

import { DataGrid } from '../DataGrid';
import ErrorBoundary from '../ErrorBoundary';
import { Filter, Filters } from '../Filters';
import { NoItems } from '../RichList/components/NoItems';
import { SortSelect } from '../SortSelect';
import { TileSkeleton } from './components/TileSkeleton';
import { ViewModeButtonGroup } from './components/ViewModeButtonGroup';

/**
 * Helper type to get all nested keys
 *
 * @example NestedKeys<{a: {b: {c: string}}}> // "a" | "a.b" | "a.b.c"
 *
 * @param T
 * @param D
 */
type NestedKeys<T, D extends number = 1> = [D] extends [never]
  ? never
  : T extends object
  ? {
      [K in keyof T & string]: NonNullable<T[K]> extends object
        ? NonNullable<T[K]> extends Function
          ? never // Skip functions/methods
          : `${K}` | `${K}.${NestedKeys<NonNullable<T[K]>, Prev[D]>}`
        : `${K}`;
    }[keyof T & string]
  : never;

// Helper type to reduce the depth at each recursive step
type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export type TypedGridColumns<
  T extends GridValidRowModel,
  C extends string | undefined = undefined
> = (GridColDef<T> & {
  field: Extract<NestedKeys<T>, string> | 'actions' | C;
})[];

const Views = ['list', 'tiles'] as const;

type ViewType = (typeof Views)[number];

interface Props<T extends GridValidRowModel> {
  data: T[] | undefined;
  columns: TypedGridColumns<T> | GridColDef<T>[];
  renderTile: (d: T) => React.ReactNode;
  height?: string;
  filters?: Filter[];
  onFilterChange?: (vars: any) => void;
  count?: number;
  controlElements?: React.ReactNode;
  initialView?: ViewType;
  hasCheckboxSelection?: boolean;
  selectionModal?: GridInputRowSelectionModel;
  onSelectionChange?: (selectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void;
  onRowClick?: GridEventListener<'rowClick'>;
  getRowClassName?: (params: GridRowClassNameParams<T>) => string;
  sortOptions?: SortOption[];
  onSortChange?: (sortBy: { [key: string]: 'asc' | 'desc' }) => void;
  /** The fetchMore property is used for a Waypoint after a given `numberOfTilesShown`(Default: 12) to fetch more data.
   *
   * It only works for Endpoints which returnObjects look like this:
   *
   * ```
   *  {
   *    data:{},
   *    pagination:{
   *      offset: number,
   *      limit: number
   *    }
   * }
   *   ```
   */
  fetchMore?: Function;
  numberOfTilesShown?: number;
  columnVisibilityModel?: GridColumnVisibilityModel;
  tilesPerRow?: number;
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
  /**
   * This is being rendered in the listView only and is used for explaining the styling of Rows in the Datagrid
   */
  explanatoryLegend?: React.ReactNode;
  isLoading?: boolean;
}

export const TilesOrListView = <T extends GridValidRowModel & { id: any }>(
  props: Props<T>
): React.ReactElement => {
  const [loadingMore, setLoadingMore] = useState(false);
  const [doneLoading, setDoneLoading] = useState(false);
  const [view, setView] = useQueryState(
    'view',
    parseAsStringLiteral(Views)
      .withOptions({ clearOnDefault: true, shallow: false })
      .withDefault(props.initialView ?? 'tiles')
  );
  const handleChange = (event: React.MouseEvent<HTMLElement>, nextView: ViewType) => {
    if (nextView) setView(nextView);
  };

  useEffect(() => {
    setDoneLoading(false);
  }, [props.data]);

  const SortConfig = () =>
    props.sortOptions &&
    props.onSortChange && (
      <Grid container justifyContent="flex-end">
        <SortSelect
          sx={{ borderWidth: 10 }}
          items={props.sortOptions}
          defaultValue={JSON.stringify(props.sortOptions[0].value)}
          onChange={e => {
            props.onSortChange && props.onSortChange(JSON.parse(e.target.value as string));
          }}
        />
      </Grid>
    );
  return (
    <>
      {!isMobile && (
        <Filters
          filterConfig={props.filters ?? []}
          onFilterChange={props.onFilterChange}
          controls={
            <Grid
              container
              spacing={2}
              style={{ display: 'flex', alignItems: 'center', margin: 0 }}
              justifyContent={'flex-end'}
            >
              {props.controlElements}
              <Grid item>
                <ViewModeButtonGroup view={view} handleChange={handleChange} />
              </Grid>
            </Grid>
          }
        />
      )}
      {props.data?.length === 0 ? (
        <NoItems />
      ) : view === 'list' ? (
        <Grid container flexDirection={'column'} gap={2}>
          <Grid
            item
            flexGrow={1}
            style={{ height: `${!!props.height ? props.height : '100%'}` }}
            component={Paper}
          >
            <DataGrid
              style={{ height: '100%' }}
              disableColumnFilter
              columns={props.columns}
              rows={props.data || []}
              rowCount={props.count ?? props.data?.length ?? 0}
              paginationMode={'client'}
              checkboxSelection={props.hasCheckboxSelection}
              onRowSelectionModelChange={props.onSelectionChange}
              rowSelectionModel={props.selectionModal}
              onRowClick={props.onRowClick}
              getRowClassName={props.getRowClassName}
              columnVisibilityModel={props.columnVisibilityModel}
              getRowHeight={props.getRowHeight}
              loading={props.isLoading}
            />
          </Grid>
          <Grid item>{props.explanatoryLegend}</Grid>
        </Grid>
      ) : (
        <>
          {!isMobile && (
            <Grid item>
              <Divider style={{ marginBottom: '0.5em' }} />
            </Grid>
          )}
          {props.sortOptions && props.onSortChange && !isMobile && <SortConfig />}
          <Grid
            container
            spacing={isMobile ? 0.5 : 1.5}
            style={{
              height: 'fit-content',
              paddingTop: isMobile ? 0 : '1em'
            }}
          >
            {props.data
              ? props.data.map(d => (
                  <Grid
                    key={d.id}
                    item
                    xs={12}
                    lg={6}
                    xl={props.tilesPerRow ? 12 / props.tilesPerRow : 4}
                  >
                    <ErrorBoundary key={d.id} boundaryTag="listingItemBoundary">
                      {props.renderTile(d)}
                    </ErrorBoundary>
                  </Grid>
                ))
              : Array.from({ length: 9 }).map((_, index) => (
                  <TileSkeleton key={index} tilesPerRow={props.tilesPerRow} />
                ))}
            {!doneLoading && (
              <Waypoint
                onEnter={() => {
                  if (props.fetchMore) {
                    setLoadingMore(true);
                    props.fetchMore({
                      variables: {
                        pagination: {
                          offset: props.data?.length,
                          limit: props.numberOfTilesShown || 12
                        }
                      },
                      updateQuery: (prev, { fetchMoreResult }) => {
                        setLoadingMore(false);
                        if (
                          !fetchMoreResult ||
                          fetchMoreResult[Object.keys(fetchMoreResult)[0]].data.length === 0
                        ) {
                          setDoneLoading(true);
                          return prev;
                        }
                        const updatedQuery = {};
                        updatedQuery[Object.keys(fetchMoreResult)[0]] = {
                          data: [
                            ...prev[Object.keys(prev)[0]].data,
                            ...fetchMoreResult[Object.keys(fetchMoreResult)[0]].data
                          ],
                          pagination: {
                            ...fetchMoreResult[Object.keys(fetchMoreResult)[0]].pagination
                          }
                        };
                        return updatedQuery;
                      }
                    });
                  }
                }}
              />
            )}
            {loadingMore && (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
};
